/**
 * Offline-queue sync orchestrator.
 *
 * Platform-agnostic: takes an `OfflineStore` snapshot (from
 * `createOfflineStore`), `createExpense` / `updateExpense` API callers, and optionally
 * a `predictCategory` caller for the sync-time cloud-categorization
 * upgrade. Encapsulates the retry-with-backoff loop, status
 * transitions, error capture, and per-group invalidation
 * notification. Web and mobile call this from thin React hook
 * wrappers — see `apps/web/src/hooks/useOfflineSync.ts` and
 * `apps/mobile/src/hooks/useOfflineSync.ts`.
 *
 * Sequential by design — concurrent retries against the same
 * idempotency-key set is asking for duplicates from a flaky server,
 * and the queue is small enough (single-digit expenses in practice)
 * that latency isn't a concern.
 */
import type {
  CreateExpenseDto,
  ExpenseResponseDto,
  PendingExpense,
  SuccessResponseDto,
  SyncResult,
  UpdateExpenseDto,
} from '@splitfiesta/shared';
import {
  EXPENSE_CREATE_ERROR_KEYS,
  LINE_ITEMS_ERROR_KEYS,
} from '@splitfiesta/shared';

import { receiptDraftDropped } from '../../scan/scanState';
import {
  type OfflineStore,
  type SyncWarningKind,
  selectOwnedExpenses,
} from '../../stores/createOfflineStore';
import { offlineExpenseService } from './OfflineExpenseService';
import {
  type CloudCategoryPrediction,
  maybeUpgradeCategoryWithCloud,
} from './maybeUpgradeCategoryWithCloud';
import { toCreateExpenseDto, toUpdateExpenseDto } from './pendingExpenseDto';
import { resolvePendingConversion } from './resolvePendingConversion';
import {
  SYNC_GROUP_GONE_ERROR,
  SYNC_RATE_UNAVAILABLE_ERROR,
  isGroupGoneSyncError,
  permanentSyncErrorFor,
  retryableSyncErrorFor,
} from './syncFailure';
import { isTransientSyncError } from './syncRetry';

/**
 * Guard: API responses may be wrapped in `SuccessResponseDto<T>` or
 * returned directly as `T`, and the wrapper can be nested one level
 * deep on some endpoints. Walks the structure and returns the inner
 * `ExpenseResponseDto` or `null` if we can't find one.
 */
function isSuccessResponse<T>(value: unknown): value is SuccessResponseDto<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'success' in value &&
    'data' in value &&
    (value as SuccessResponseDto<T>).success === true
  );
}

function isExpenseResponseDto(value: unknown): value is ExpenseResponseDto {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'groupId' in value &&
    'amount' in value &&
    typeof (value as ExpenseResponseDto).id === 'string'
  );
}

function extractExpenseFromResponse(
  response: unknown
): ExpenseResponseDto | null {
  if (isExpenseResponseDto(response)) return response;
  if (isSuccessResponse<unknown>(response)) {
    const inner = response.data;
    if (isExpenseResponseDto(inner)) return inner;
    if (isSuccessResponse<unknown>(inner)) {
      const deeper = inner.data;
      if (isExpenseResponseDto(deeper)) return deeper;
    }
  }
  return null;
}

export interface RunOfflineSyncArgs {
  /** Store handle — typically `useOfflineStore.getState()`. */
  store: OfflineStore;
  /** Caller's "I'm online" snapshot. If `false`, sync no-ops. */
  isOnline: boolean;
  /**
   * The signed-in user. The server attributes a POST to the token's user, so
   * only rows this user queued may be sent; every other row is left as is.
   */
  ownerId: string;
  /**
   * Wrapper around `ApiClientService.expenses.create`. Should return
   * the raw API response (wrapped or unwrapped — `extractExpenseFromResponse`
   * handles both).
   */
  createExpense: (data: CreateExpenseDto) => Promise<unknown>;
  /**
   * Wrapper around `ApiClientService.expenses.update`. Brings the server copy
   * up to date when the user edited a row while its POST was in flight.
   */
  updateExpense: (id: string, data: UpdateExpenseDto) => Promise<unknown>;
  /**
   * Optional sync-time cloud category upgrade. When supplied, each
   * pending expense is first passed through
   * `maybeUpgradeCategoryWithCloud` before POST. Omit to skip the
   * upgrade entirely (useful in tests or environments without an
   * Anthropic key).
   */
  predictCategory?: (input: {
    title: string;
    locale: string;
  }) => Promise<CloudCategoryPrediction | null>;
  /** BCP 47 locale tag (e.g. 'en', 'es-CO'). Required when `predictCategory` is set. */
  locale?: string;
  /**
   * Wrapper around `ApiClientService.exchangeRates.get`, unwrapped to
   * `{ rate, rateDate }`. Required to sync any expense queued with
   * `pendingConversion: true` (a foreign-currency expense created
   * offline, stored in its original currency) — `syncOne` calls this
   * with the pending item's `{ currency, targetCurrency, date }` to
   * get a fresh rate, then converts via `resolvePendingConversion`
   * before POST. Omit only when the queue can never contain a
   * `pendingConversion` item (e.g. some test setups); a
   * `pendingConversion` item synced without it fails non-transiently
   * rather than posting original-currency amounts as if converted.
   */
  getExchangeRate?: (p: {
    from: string;
    to: string;
    date: string;
  }) => Promise<{ rate: number; rateDate: string }>;
  /**
   * Called once per group whose expenses got synced. The caller wires
   * this to React Query's `invalidateQueries` so cached lists refresh.
   * Async — `runOfflineSync` awaits it so a caller can chain async
   * invalidations.
   */
  onGroupSynced?: (groupId: string) => void | Promise<void>;
  /** Max retry attempts per expense before we stop retrying. */
  maxRetries?: number;
}

/**
 * Re-entrancy guard. Module-level so multiple hook instances (one per
 * mount of `OfflineSyncInitializer`) can't race each other. The web
 * hook used a `useRef` for this; promoting to module scope is
 * stricter — exactly one sync in flight per app process.
 */
let syncInProgress = false;

export async function runOfflineSync(
  args: RunOfflineSyncArgs
): Promise<SyncResult> {
  const {
    store,
    isOnline,
    ownerId,
    createExpense,
    updateExpense,
    predictCategory,
    locale,
    onGroupSynced,
    maxRetries = 3,
    getExchangeRate,
  } = args;

  if (syncInProgress || !isOnline) {
    return {
      success: false,
      syncedCount: 0,
      failedCount: 0,
      errors: [],
    };
  }

  // Past the guard above, no sync is in flight in this JS context, so any
  // item still marked 'syncing' is left over from a run that died mid-POST
  // (or from a store hydrated before that reconciliation existed). Selecting
  // from the returned queue rather than `store.pendingExpenses`: `store` is a
  // snapshot, and zustand hands the revert back on a new array.
  const queue = selectOwnedExpenses(
    { pendingExpenses: store.reconcileStaleSyncing() },
    ownerId
  );

  const retryable = offlineExpenseService.getRetryableExpenses(
    queue,
    maxRetries
  );
  if (retryable.length === 0) {
    return { success: true, syncedCount: 0, failedCount: 0, errors: [] };
  }

  syncInProgress = true;
  store.setSyncing(true);
  store.setLastSyncAttempt(Date.now());

  const result: SyncResult = {
    success: true,
    syncedCount: 0,
    failedCount: 0,
    errors: [],
  };
  const groupsToInvalidate = new Set<string>();

  try {
    const ctx: SyncContext = {
      store,
      ownerId,
      createExpense,
      updateExpense,
      getExchangeRate,
    };
    for (const queued of retryable) {
      // Respect exponential backoff for previously-failed entries.
      if (!offlineExpenseService.shouldRetryWithBackoff(queued)) continue;

      // Sync-time cloud-categorization upgrade. Best-effort — the
      // helper swallows all errors and returns the original expense
      // unchanged on failure. We persist the upgraded category back
      // to the store BEFORE the POST so a retry after a transient
      // network failure doesn't re-spend on the upgrade.
      if (predictCategory && locale) {
        const upgraded = await maybeUpgradeCategoryWithCloud({
          expense: queued,
          locale,
          predictCategory,
        });
        const current = readRow(ctx, queued.localId);
        // A user edit made during the prediction outranks it.
        if (
          upgraded !== queued &&
          current &&
          revisionOf(current) === revisionOf(queued)
        ) {
          store.updatePendingExpense(queued.localId, {
            category: upgraded.category,
            subcategory: upgraded.subcategory,
            predictedCategorySource: upgraded.predictedCategorySource,
            predictedCategoryConfidence: upgraded.predictedCategoryConfidence,
          });
        }
      }

      // `queued` is the run-start snapshot; post what the row holds now.
      const expense = readRow(ctx, queued.localId);
      if (!expense) continue;

      const outcome = await syncOne(expense, ctx);

      if (outcome.kind === 'synced') {
        store.markExpenseSynced(expense.localId, outcome.serverId);
        if (outcome.warning) {
          store.setSyncWarning({
            localId: expense.localId,
            serverId: outcome.serverId,
            warning: outcome.warning,
          });
        }
        groupsToInvalidate.add(expense.groupId);
        result.syncedCount++;
      } else if (outcome.kind === 'retry') {
        // Connectivity / backend-down (5xx) — the write never landed — or
        // an edit still owed to the server. Revert 'syncing' → 'pending'
        // so it stays retryable WITHOUT consuming the maxRetries budget: a
        // backend outage of any length must never strand a queued expense
        // as permanently 'failed'. The caller's flush trigger backs off
        // between cycles.
        store.updateExpenseStatus(expense.localId, 'pending');
      } else {
        store.updateExpenseStatus(expense.localId, 'failed', outcome.error);
        // No retry or edit can bring the group back: spend the budget so
        // the row waits for the user's Discard.
        if (outcome.permanent) {
          store.updatePendingExpense(expense.localId, {
            retryCount: maxRetries,
          });
        }
        result.failedCount++;
        result.errors.push({ localId: expense.localId, error: outcome.error });
      }
    }

    if (onGroupSynced) {
      for (const groupId of groupsToInvalidate) {
        await onGroupSynced(groupId);
      }
    }

    result.success = result.failedCount === 0;
    return result;
  } finally {
    store.setSyncing(false);
    syncInProgress = false;
  }
}

interface SyncContext {
  store: OfflineStore;
  ownerId: string;
  createExpense: RunOfflineSyncArgs['createExpense'];
  updateExpense: RunOfflineSyncArgs['updateExpense'];
  getExchangeRate?: RunOfflineSyncArgs['getExchangeRate'];
}

export type SyncOutcome =
  | { kind: 'synced'; serverId: string; warning?: SyncWarningKind }
  | { kind: 'retry' }
  | { kind: 'failed'; error: string; permanent?: boolean };

const revisionOf = (expense: PendingExpense): number => expense.revision ?? 0;

/** The live row: `store` is a snapshot, but its actions read current state. */
function readRow(
  ctx: SyncContext,
  localId: string
): PendingExpense | undefined {
  return ctx.store.getExpenseByLocalId(localId, ctx.ownerId);
}

/**
 * Transient (network / 5xx) failures stay retryable without burning budget.
 * So does a write race: a line-items conflict on a PATCH, or a create whose
 * transaction lost to a concurrent write. Nothing landed, and the next run
 * simply writes again under the same idempotency key.
 */
function outcomeOf(err: unknown, request: 'create' | 'patch'): SyncOutcome {
  if (isTransientSyncError(err)) return { kind: 'retry' };
  const translationKey = (err as { translationKey?: unknown } | null)
    ?.translationKey;
  if (
    (request === 'patch' &&
      translationKey === LINE_ITEMS_ERROR_KEYS.conflict) ||
    (request === 'create' &&
      translationKey === EXPENSE_CREATE_ERROR_KEYS.conflict)
  ) {
    return { kind: 'retry' };
  }
  if (isGroupGoneSyncError(err)) {
    return { kind: 'failed', error: SYNC_GROUP_GONE_ERROR, permanent: true };
  }
  const permanentError = permanentSyncErrorFor(err);
  if (permanentError) {
    return { kind: 'failed', error: permanentError, permanent: true };
  }
  const retryableError = retryableSyncErrorFor(err);
  if (retryableError) return { kind: 'failed', error: retryableError };
  return {
    kind: 'failed',
    error: err instanceof Error ? err.message : 'Unknown sync error',
  };
}

async function syncOne(
  expense: PendingExpense,
  ctx: SyncContext
): Promise<SyncOutcome> {
  ctx.store.updateExpenseStatus(expense.localId, 'syncing');

  if (expense.needsServerPatch && expense.serverId) {
    return patchServer(expense, expense.serverId, ctx);
  }

  const prepared = await toPostable(expense, ctx.getExchangeRate);
  if ('outcome' in prepared) return prepared.outcome;

  let serverId: string;
  let receiptDropped: boolean;
  try {
    const created = extractExpenseFromResponse(
      await ctx.createExpense(prepared.dto)
    );
    if (!created) {
      return {
        kind: 'failed',
        error: 'Invalid response from server - no expense id',
      };
    }
    serverId = created.id;
    // The server saves the expense without a receipt when the queued draft
    // expired, and says so only by leaving `receipt` out.
    receiptDropped = receiptDraftDropped({
      sentDraftId: prepared.dto.receiptDraftId,
      savedReceipt: created.receipt,
    });
  } catch (err) {
    return outcomeOf(err, 'create');
  }
  const synced: SyncOutcome = {
    kind: 'synced',
    serverId,
    ...(receiptDropped && { warning: 'receiptDropped' as const }),
  };

  const current = readRow(ctx, expense.localId);
  if (!current || revisionOf(current) === revisionOf(expense)) {
    return synced;
  }
  // Edited during the POST. Recorded before the PATCH so that, if the PATCH
  // can't land now, the next run patches instead of creating a duplicate.
  ctx.store.updatePendingExpense(expense.localId, {
    serverId,
    needsServerPatch: true,
  });
  const marked = readRow(ctx, expense.localId);
  if (!marked) return synced;
  const patched = await patchServer(marked, serverId, ctx);
  if (patched.kind === 'synced') return synced;
  // The expense exists on the server either way; a PATCH left for the next
  // run would otherwise lose the warning, since that run never POSTs.
  if (receiptDropped) {
    ctx.store.setSyncWarning({
      localId: expense.localId,
      serverId,
      warning: 'receiptDropped',
    });
  }
  return patched;
}

async function patchServer(
  expense: PendingExpense,
  serverId: string,
  ctx: SyncContext
): Promise<SyncOutcome> {
  const prepared = await toPostable(expense, ctx.getExchangeRate);
  if ('outcome' in prepared) return prepared.outcome;
  try {
    await ctx.updateExpense(serverId, toUpdateExpenseDto(prepared.dto));
  } catch (err) {
    return outcomeOf(err, 'patch');
  }
  const current = readRow(ctx, expense.localId);
  // Edited again during the PATCH: the row stays queued for one more PATCH.
  if (current && revisionOf(current) !== revisionOf(expense)) {
    return { kind: 'retry' };
  }
  return { kind: 'synced', serverId };
}

async function toPostable(
  expense: PendingExpense,
  getExchangeRate?: RunOfflineSyncArgs['getExchangeRate']
): Promise<{ dto: CreateExpenseDto } | { outcome: SyncOutcome }> {
  if (!expense.pendingConversion) return { dto: toCreateExpenseDto(expense) };
  // Programmer-error guard, not user copy — this can only happen
  // if a caller wires the offline queue without ever supplying a
  // rate provider. Never post the original-currency amounts as if
  // they were already converted.
  if (!getExchangeRate) {
    return {
      outcome: {
        kind: 'failed',
        error: 'Sync misconfigured: no exchange-rate provider',
      },
    };
  }
  if (!expense.date) {
    return {
      outcome: {
        kind: 'failed',
        error: 'Sync misconfigured: pending expense missing date',
      },
    };
  }
  try {
    const rate = await getExchangeRate({
      from: expense.currency,
      to: expense.targetCurrency!,
      date: expense.date,
    });
    return { dto: resolvePendingConversion(expense, rate) };
  } catch (err) {
    // A transient lookup failure (network/5xx) keeps the item
    // retryable exactly like a transient create failure. A
    // permanent one (404 no rate for that date, any 4xx) fails
    // the item with the i18n key the failed-sync banner renders.
    if (isTransientSyncError(err)) return { outcome: { kind: 'retry' } };
    return { outcome: { kind: 'failed', error: SYNC_RATE_UNAVAILABLE_ERROR } };
  }
}
